import React from 'react';

import { Link } from '@ergeon/core-components';

// Matches URLs (http/https) and email addresses
const LINK_REGEX = /(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

// Truncate long URLs for display
const truncateUrl = (url: string, maxLength: number = 50): string => {
  if (url.length <= maxLength) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const path = urlObj.pathname + urlObj.search + urlObj.hash;

    if (domain.length > maxLength - 10) {
      return `${url.substring(0, maxLength - 3)}...`;
    }

    const availableSpace = maxLength - domain.length - 10;

    if (availableSpace > 0 && path.length > availableSpace) {
      const partSize = Math.floor(availableSpace / 2);
      const truncatedPath = `${path.substring(0, partSize)}...${path.substring(path.length - partSize)}`;
      return `${urlObj.protocol}//${domain}${truncatedPath}`;
    }

    return `${url.substring(0, maxLength - 3)}...`;
  } catch {
    return `${url.substring(0, maxLength - 3)}...`;
  }
};

// Convert text with URLs and emails into React elements with clickable Link components
export const formatTextWithLinks = (text: string | null | undefined): React.ReactNode[] => {
  if (!text) {
    return [];
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  LINK_REGEX.lastIndex = 0;

  while ((match = LINK_REGEX.exec(text)) !== null) {
    const matchedText = match[0];
    const index = match.index;

    // Add text before the match
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index));
    }

    const isUrl = match[1] !== undefined;
    const isEmail = match[2] !== undefined;

    if (isUrl) {
      const displayText = truncateUrl(matchedText);
      parts.push(
        <Link href={matchedText} key={`link-${index}`} rel="noopener noreferrer" target="_blank">
          {displayText}
        </Link>,
      );
    } else if (isEmail) {
      parts.push(
        <Link href={`mailto:${matchedText}`} key={`email-${index}`}>
          {matchedText}
        </Link>,
      );
    }

    lastIndex = index + matchedText.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  if (parts.length === 0) {
    return [text];
  }

  return parts;
};
